import React, { useState } from 'react';
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box, Button, Link, TextField, Typography,
  Alert, InputAdornment, IconButton, CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { PasswordStrengthIndicator } from '../../components/common/PasswordStrengthIndicator';
import { useResetPasswordMutation } from '../../store/api/authApi';

const schema = yup.object({
  password: yup
    .string()
    .min(8, 'At least 8 characters')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

type FormData = yup.InferType<typeof schema>;

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(false);

  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const password = watch('password', '');

  const onSubmit = async (data: FormData) => {
    if (!token) {
      setServerError('Reset token is missing. Please use the link from your email.');
      return;
    }
    setServerError('');
    try {
      await resetPassword({ token, password: data.password }).unwrap();
      setSuccess(true);
      setTimeout(() => navigate('/auth/login', { replace: true }), 3000);
    } catch (err: any) {
      setServerError(err?.data?.detail ?? 'Reset failed. The link may have expired.');
    }
  };

  if (!token) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Invalid reset link. Please request a new one.
        </Alert>
        <Link component={RouterLink} to="/auth/forgot-password" fontWeight={600}>
          Request new reset link
        </Link>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CheckCircle sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" fontWeight={700} mb={1}>Password updated</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Your password has been reset. Redirecting you to sign in…
        </Typography>
        <Link component={RouterLink} to="/auth/login" fontWeight={600}>
          Sign in now
        </Link>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} textAlign="center" mb={0.5}>
        Set new password
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
        Choose a strong password for your account
      </Typography>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          {...register('password')}
          label="New password"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          autoComplete="new-password"
          autoFocus
          error={!!errors.password}
          helperText={errors.password?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 0.5 }}
        />
        <PasswordStrengthIndicator password={password} />

        <TextField
          {...register('confirmPassword')}
          label="Confirm new password"
          type={showConfirm ? 'text' : 'password'}
          fullWidth
          autoComplete="new-password"
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end" size="small">
                  {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mt: 2, mb: 2 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{ mb: 2, py: 1.25, fontWeight: 600, textTransform: 'none', fontSize: '1rem' }}
        >
          {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Update password'}
        </Button>
      </Box>

      <Typography variant="body2" textAlign="center" color="text.secondary">
        <Link component={RouterLink} to="/auth/login" fontWeight={600}>Back to sign in</Link>
      </Typography>
    </Box>
  );
};
